import type { LightningConfig, LightningLine, LightningPoint } from './types';

export const LIGHTNING_GROUP_SCALE = 2.2;
export const LIGHTNING_INNER_COLOR = '#fff4df';

export const createLightningLine = (id: string): LightningLine => ({
  id,
  enabled: true,
  start: [0, 0, 0],
  end: [0.5, 0.3, 0],
  delay: 0,
  lifetime: 0.98,
  revealSeconds: 0.07,
  brightness: 6.2,
  thickness: 0.0016,
  jag: 0.04,
  detail: 16,
  flickerHz: 9,
  color: '#ff9d58',
  innerColor: LIGHTNING_INNER_COLOR,
});

// Five authoring seeds, not a production look. Each location is a whole
// choreography: a main strike followed by a shorter, delayed branch.
const positions: LightningPoint[] = [
  [-0.45, -0.2, -0.05],
  [0.4, -0.3, -0.05],
  [-0.35, 0.35, -0.05],
  [0.3, 0.3, -0.05],
  [0, -0.45, -0.05],
];

export const LIGHTNING_CONFIG: LightningConfig = {
  gapMin: 0.18,
  gapMax: 0.45,
  groups: positions.map((position, index) => ({
    id: `spawn-${index + 1}`,
    name: `Spawn ${index + 1}`,
    enabled: true,
    position,
    rotationDeg: [0, 0, index * 65],
    scale: LIGHTNING_GROUP_SCALE,
    // Approximate the two half-speed references: strands join at different
    // times, fan out, briefly go dark, then restrike and gather again.
    lines: Array.from({ length: 4 }, (_, strand) => ({
      ...createLightningLine(`strand-${index + 1}-${strand + 1}`),
      start: [0, strand * 0.018, 0] as LightningPoint,
      end: [0.72 + strand * 0.06, 0.24 + strand * 0.035, 0] as LightningPoint,
      delay: [0, 0.07, 0.16, 0.28][strand],
      lifetime: 0.98 - [0, 0.07, 0.16, 0.28][strand],
      brightness: [5.2, 6.2, 4.1, 5.6][strand],
      jag: 0.018 + strand * 0.004,
      flickerHz: 7 + strand * 1.5,
      keyframes: [
        [0, 0.5, 0],
        [0.045, 1, 0.015],
        [0.17, 0.85, 0.07],
        [0.2, 0, 0.09],
        [0.235, 0, 0.11],
        [0.25, 1, 0.12],
        [0.4, 0.75, 0.19],
        [0.43, strand % 2 ? 0 : 0.6, 0.2],
        [0.46, strand % 2 ? 0 : 0.7, 0.21],
        [0.475, 1, 0.215],
        [0.62, 0.85, 0.25],
        [0.7, 0, 0.27],
        [0.98, 0, 0.28],
      ].map(([time, intensity, drift]) => ({
        time: (time * (0.98 - [0, 0.07, 0.16, 0.28][strand])) / 0.98,
        intensity,
        endOffset: [0, drift * (strand % 2 ? 1 : -1), 0] as LightningPoint,
      })),
    })),
  })),
};
